import {
    Breadcrumb,
    Layout,
    Form,
    Input,
    DatePicker,
    Select,
    Button,
    Row,
    Col,
    Card,
    Switch,
    message,
    Spin,
    Table,
    Typography,
    Modal,
    Space,
    Popconfirm,
    InputNumber,
    Tabs,
    List
} from "antd";
import {
    EditOutlined,
    DeleteOutlined,
    PlusOutlined
} from '@ant-design/icons';
import { Content } from "antd/es/layout/layout";
import { Link, useParams, useNavigate } from "react-router-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { APPBACKGROUND_COLOR } from "../../appsettings";
import Headerdiv from "../../layout/header";
import Footerdiv from "../../layout/footer";
import { TSHIRT_SIZES, SHOE_SIZES, TROUSER_SIZES } from "../../../constant/constant";
import axiosClient from "../../../api/axiosClient";

const { Title } = Typography;
const { Option } = Select;

export const MemberEdit = () => {
    const [form] = Form.useForm();
    const [metricsForm] = Form.useForm();
    const { id } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [memberName, setMemberName] = useState("");

    // Metrics State
    const [metrics, setMetrics] = useState([]);
    const [metricsLoading, setMetricsLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });
    const [isMetricsModalOpen, setIsMetricsModalOpen] = useState(false);
    const [selectedMetricForEdit, setSelectedMetricForEdit] = useState(null);

    // Trainer Assignment State
    const [assignedTrainers, setAssignedTrainers] = useState([]);
    const [trainersList, setTrainersList] = useState([]);
    const [assignModalOpen, setAssignModalOpen] = useState(false);
    const [selectedTrainerId, setSelectedTrainerId] = useState(null);
    const [assignmentLoading, setAssignmentLoading] = useState(false);

    // Relationship State
    const [relationships, setRelationships] = useState([]);
    const [relationshipsLoading, setRelationshipsLoading] = useState(false);
    const [relationshipModalOpen, setRelationshipModalOpen] = useState(false);
    const [memberList, setMemberList] = useState([]);
    const [selectedLinkedMemberId, setSelectedLinkedMemberId] = useState(null);
    const [relationType, setRelationType] = useState(null);
    const [relationFormLoading, setRelationFormLoading] = useState(false);

    const USERID = localStorage.getItem("USERID");
    // const TOKEN = localStorage.getItem("AUTHCODE"); // Removed

    // 1. Fetch Member Data
    useEffect(() => {
        const fetchMemberData = async () => {
            if (!id) return;
            setLoading(true);
            try {
                const response = await axiosClient.get(`/member/get_member_by_id.php?id=${id}`);
                const data = response.data; // Axios automatically parses JSON

                if (data.status === "success") {
                    const member = data.data;
                    setMemberName(member.name);

                    // Populate form
                    form.setFieldsValue({
                        ...member,
                        birthday: member.birthday ? dayjs(member.birthday) : null,
                        status: member.status === true || member.status === "1" || member.status === 1
                    });
                } else {
                    message.error(data.message || "Failed to load member data");
                    navigate("/members/view");
                }
            } catch (error) {
                console.error("Fetch Error:", error);
                message.error(error.response?.data?.message || "Failed to load member data");
            } finally {
                setLoading(false);
            }
        };

        fetchMemberData();
    }, [id, form, navigate]);

    // 2. Update Member Data (Main Form)
    const onFinish = async (values) => {
        const payload = {
            ...values,
            birthday: values.birthday ? values.birthday.format("YYYY-MM-DD") : null,
            status: values.status ? true : false,
            update_by: USERID,
            target_id: id
        };

        try {
            const response = await axiosClient.post(
                `/member/update_member_by_id.php`,
                payload
            );

            const data = response.data; // Axios automatically parses JSON

            if (data.status === "success") {
                message.success(data.message);
                navigate("/members/view");
            } else {
                message.error(data.message);
            }
        } catch (error) {
            console.error("API Error:", error);
            message.error("Failed to connect to server.");
        }
    };

    // 3. Metrics: Fetch Data
    const fetchMetrics = async (page = 1, pageSize = 10) => {
        if (!id) return;
        setMetricsLoading(true);
        try {
            const response = await axiosClient.get(
                `/member/metric/get_all_member_metrics_by_id.php?member_id=${id}&page=${page}&pageSize=${pageSize}`
            );
            const data = response.data; // Axios automatically parses JSON

            if (data.status === "success") {
                setMetrics(data.data);
                setPagination({
                    current: page,
                    pageSize: pageSize,
                    total: data.total || 0,
                });
            }
        } catch (error) {
            console.error("Metrics fetch error:", error);
            message.error("Failed to fetch metrics.");
        } finally {
            setMetricsLoading(false);
        }
    };

    useEffect(() => {
        if (id) fetchMetrics(pagination.current, pagination.pageSize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleTableChange = (newPagination) => {
        fetchMetrics(newPagination.current, newPagination.pageSize);
    };

    // --- Trainer Assignment Logic ---
    const fetchAssignedTrainers = async () => {
        if (!id) return;
        try {
            const response = await axiosClient.get(`/member/get_assigned_trainers.php?member_id=${id}`);
            if (response.data.status === "success") {
                setAssignedTrainers(response.data.data);
            }
        } catch (error) {
            console.error("Error fetching assigned trainers:", error);
        }
    };

    const fetchTrainersDropdown = async (search = "") => {
        try {
            const response = await axiosClient.get(`/trainer/get_all_trainers_dropdown.php?search=${search}`);
            if (response.data.status === "success") {
                setTrainersList(response.data.data);
            }
        } catch (error) {
            console.error("Error fetching trainers:", error);
        }
    };

    useEffect(() => {
        if (id) {
            fetchAssignedTrainers();
        }
    }, [id]);

    const handleAssignTrainer = async () => {
        if (!selectedTrainerId) {
            message.error("Please select a trainer");
            return;
        }
        setAssignmentLoading(true);
        try {
            const response = await axiosClient.post("/member/assign_trainer.php", {
                member_id: id,
                trainer_id: selectedTrainerId,
                by: USERID
            });
            if (response.data.status === "success") {
                message.success("Trainer assigned successfully");
                setAssignModalOpen(false);
                setSelectedTrainerId(null);
                fetchAssignedTrainers();
            } else {
                message.error(response.data.message);
            }
        } catch (error) {
            message.error(error.response?.data?.message || "Failed to assign trainer");
        } finally {
            setAssignmentLoading(false);
        }
    };

    const handleRemoveTrainer = async (trainerId) => {
        try {
            const response = await axiosClient.post("/member/remove_trainer.php", {
                member_id: id,
                trainer_id: trainerId
            });
            if (response.data.status === "success") {
                message.success("Trainer removed successfully");
                fetchAssignedTrainers();
            } else {
                message.error(response.data.message);
            }
        } catch (error) {
            message.error(error.response?.data?.message || "Failed to remove trainer");
        }
    };

    // --- Relationship Logic ---
    const fetchRelationships = async () => {
        if (!id) return;
        setRelationshipsLoading(true);
        try {
            const response = await axiosClient.get(`/member/get_relationships.php?member_id=${id}`);
            if (response.data.status === "success") {
                setRelationships(response.data.data);
            }
        } catch (error) {
            console.error("Error fetching relationships:", error);
        } finally {
            setRelationshipsLoading(false);
        }
    };

    const fetchMembersDropdown = async (search = "") => {
        try {
            const response = await axiosClient.get(`/member/get_all_members_dropdown.php?search=${search}`);
            if (response.data.status === "success") {
                // Filter out current member
                setMemberList(response.data.data.filter(m => m.id != id));
            }
        } catch (error) {
            console.error("Error fetching members:", error);
        }
    };

    useEffect(() => {
        if (id) {
            fetchRelationships();
        }
    }, [id]);

    const handleAddRelationship = async () => {
        if (!selectedLinkedMemberId || !relationType) {
            message.error("Please select a member and relationship type");
            return;
        }
        setRelationFormLoading(true);
        try {
            const response = await axiosClient.post("/member/add_relationship.php", {
                member_id: id,
                linked_member_id: selectedLinkedMemberId,
                relation_type: relationType
            });
            if (response.data.status === "success") {
                message.success("Relationship added successfully");
                setRelationshipModalOpen(false);
                setSelectedLinkedMemberId(null);
                setRelationType(null);
                fetchRelationships();
            } else {
                message.error(response.data.message);
            }
        } catch (error) {
            message.error(error.response?.data?.message || "Failed to add relationship");
        } finally {
            setRelationFormLoading(false);
        }
    };

    const handleRemoveRelationship = async (relationId) => {
        try {
            const response = await axiosClient.post("/member/delete_relationship.php", {
                relation_id: relationId
            });
            if (response.data.status === "success") {
                message.success("Relationship removed successfully");
                fetchRelationships();
            } else {
                message.error(response.data.message);
            }
        } catch (error) {
            message.error(error.response?.data?.message || "Failed to remove relationship");
        }
    };
    // ---------------------------------
    // ---------------------------------

    // 4. Metrics: Modal Actions
    const openAddMetricsModal = () => {
        setSelectedMetricForEdit(null);
        metricsForm.resetFields();
        metricsForm.setFieldsValue({ date: dayjs() });
        setIsMetricsModalOpen(true);
    };

    const openEditMetricsModal = (metric) => {
        setSelectedMetricForEdit(metric);
        metricsForm.setFieldsValue({
            ...metric,
            date: metric.date ? dayjs(metric.date) : null,
        });
        setIsMetricsModalOpen(true);
    };

    const calculateBMI = (_, allValues) => {
        const { weight, height } = allValues;
        if (weight && height) {
            // Height in meters
            const h = height / 100;
            const bmiValue = (weight / (h * h)).toFixed(2);
            metricsForm.setFieldsValue({ bmi: bmiValue });
        }
    };

    // 5. Metrics: Save (Add/Update)
    const handleMetricsSubmit = async (values) => {
        const basePayload = {
            ...values,
            date: values.date ? values.date.format("YYYY-MM-DD HH:mm:ss") : null,
            member_id: id,
        };

        let url = "";
        let finalPayload = {};

        if (selectedMetricForEdit) {
            url = `/member/metric/update_member_metric_by_id.php`;
            finalPayload = {
                ...basePayload,
                metric_id: selectedMetricForEdit.id,
                updated_by: USERID
            };
        } else {
            url = `/member/metric/add_member_metric.php`;
            finalPayload = {
                ...basePayload,
                created_by: USERID
            };
        }

        try {
            setLoading(true);
            const response = await axiosClient.post(url, finalPayload);
            const data = response.data;

            if (data.status === "success" || data.status === true) {
                message.success(selectedMetricForEdit ? "Metric updated" : "Metric added");
                setIsMetricsModalOpen(false);
                fetchMetrics(pagination.current, pagination.pageSize);
            } else {
                message.error(data.message || "Operation failed");
            }
        } catch (error) {
            console.error("Metric Save Error:", error);
            message.error("System error saving metric");
        } finally {
            setLoading(false);
        }
    };

    // 6. Metrics: Delete
    const deleteMetrics = async (metricId) => {
        try {
            setLoading(true);
            const response = await axiosClient.post(
                `/member/metric/delete_member_metric_by_id.php`,
                {
                    metric_id: metricId,
                    member_id: id,
                    updated_by: USERID
                }
            );
            const data = response.data;
            if (data.status === true || data.status === "success") {
                message.success("Metric deleted successfully");
                fetchMetrics(pagination.current, pagination.pageSize);
            } else {
                message.error(data.message || "Failed to delete metric");
            }
        } catch (error) {
            console.error("Metrics error:", error);
            message.error("Failed to delete metric");
        } finally {
            setLoading(false);
        }
    };

    const metricColumns = [
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            render: (text) => text ? dayjs(text).format("DD MMM YYYY hh:mm A") : "N/A",
        },
        {
            title: 'Weight (kg)',
            dataIndex: 'weight',
            key: 'weight',
        },
        {
            title: 'Height (cm)',
            dataIndex: 'height',
            key: 'height',
        },
        {
            title: 'BMI',
            dataIndex: 'bmi',
            key: 'bmi',
        },
        {
            title: 'Body Fat (%)',
            dataIndex: 'body_fat',
            key: 'body_fat',
        },
        {
            title: 'Muscle Mass (kg)',
            dataIndex: 'muscle_mass',
            key: 'muscle_mass',
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button
                        type="primary"
                        icon={<EditOutlined />}
                        size="small"
                        style={{
                            backgroundColor: "#FFC107",
                            borderColor: "#FFC107",
                            color: "white",
                        }}
                        onClick={() => openEditMetricsModal(record)}
                    />
                    <Popconfirm
                        title="Delete Metric"
                        description="Are you sure to delete this metric?"
                        onConfirm={() => deleteMetrics(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button type="primary" danger icon={<DeleteOutlined />} size="small" />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <Layout style={{ backgroundColor: APPBACKGROUND_COLOR, minHeight: "100vh" }}>
            <Headerdiv />
            <Content style={{ padding: "10px" }}>
                <Breadcrumb style={{ marginBottom: 10 }}>
                    <Breadcrumb.Item>
                        <Link to={"/members/view"}>Members</Link>
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>Edit Member</Breadcrumb.Item>
                </Breadcrumb>

                <Spin spinning={loading} tip="Loading Member Details...">
                    <Form layout="vertical" form={form} onFinish={onFinish}>
                        <Card title={`Edit Member: ${id || ''}`}>
                            <Row gutter={[16, 16]}>

                                <Col xs={24} sm={12} md={4}>
                                    <Form.Item
                                        name="member_id"
                                        label="Member ID"
                                        rules={[{ required: true, message: "Member ID is required" }]}
                                    >
                                        <Input placeholder="Enter Member ID" disabled />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12} md={4}>
                                    <Form.Item
                                        name="nic"
                                        label="NIC"
                                        rules={[
                                            { required: true, message: "NIC is required" },
                                            {
                                                pattern: /^([0-9]{9}[VvXx]|[0-9]{12})$/,
                                                message: "Enter a valid Sri Lankan NIC",
                                            },
                                        ]}
                                    >
                                        <Input placeholder="Enter NIC" />
                                    </Form.Item>
                                </Col>

                                <Col xs={24} sm={12} md={8}>
                                    <Form.Item
                                        name="name"
                                        label="Full Name"
                                        rules={[{ required: true, message: "Name is required" }]}
                                    >
                                        <Input placeholder="Enter Name" />
                                    </Form.Item>
                                </Col>

                                <Col xs={12} sm={6} md={4}>
                                    <Form.Item
                                        name="birthday"
                                        label="Birthday"
                                        rules={[{ required: true, message: "Birthday is required" }]}
                                    >
                                        <DatePicker style={{ width: "100%" }} />
                                    </Form.Item>
                                </Col>

                                <Col xs={12} sm={6} md={4}>
                                    <Form.Item
                                        name="gender"
                                        label="Gender"
                                        rules={[{ required: true, message: "Select Gender" }]}
                                    >
                                        <Select placeholder="Select Gender">
                                            <Option value="M">Male</Option>
                                            <Option value="F">Female</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>

                                <Col xs={12} sm={12} md={4}>
                                    <Form.Item
                                        name="contact_no"
                                        label="Contact Number"
                                        rules={[
                                            { required: true, message: "Contact is required" },
                                            {
                                                pattern: /^(?:0\d{9}|\+94\d{9})$/,
                                                message: "Enter a valid Sri Lankan phone number",
                                            },
                                        ]}
                                    >
                                        <Input placeholder="Enter Contact Number" />
                                    </Form.Item>
                                </Col>

                                <Col xs={12} sm={12} md={4}>
                                    <Form.Item
                                        name="emergency_contact"
                                        label="Emergency Contact"
                                        rules={[
                                            { required: true, message: "Emergency Contact is required" },
                                            {
                                                pattern: /^(?:0\d{9}|\+94\d{9})$/,
                                                message: "Enter a valid Sri Lankan phone number",
                                            },
                                        ]}
                                    >
                                        <Input placeholder="Enter Emergency Contact" />
                                    </Form.Item>
                                </Col>

                                <Col xs={16} sm={16} md={16}>
                                    <Form.Item
                                        name="address"
                                        label="Address"
                                        rules={[{ required: true, message: "Address is required" }]}
                                    >
                                        <Input.TextArea rows={1} placeholder="Enter Address" />
                                    </Form.Item>
                                </Col>

                                {/* HEALTH NOTES */}
                                <Col xs={24}>
                                    <Form.Item name="health_conditions" label="Health Notes">
                                        <ReactQuill theme="snow" />
                                    </Form.Item>
                                </Col>

                                {/* Sizes */}
                                <Col xs={12} sm={6} md={6}>
                                    <Form.Item name="tshirt_size" label="T-Shirt Size">
                                        <Select placeholder="Select T-Shirt Size" allowClear>
                                            {TSHIRT_SIZES.map(size => <Option key={size.value} value={size.value}>{size.label}</Option>)}
                                        </Select>
                                    </Form.Item>
                                </Col>

                                <Col xs={12} sm={6} md={6}>
                                    <Form.Item name="shoe_size" label="Shoe Size">
                                        <Select placeholder="Select Shoe Size" allowClear>
                                            {SHOE_SIZES.map(size => <Option key={size.value} value={size.value}>{size.label}</Option>)}
                                        </Select>
                                    </Form.Item>
                                </Col>

                                <Col xs={12} sm={6} md={6}>
                                    <Form.Item name="trouser_size" label="Trouser Size">
                                        <Select placeholder="Select Trouser Size" allowClear>
                                            {TROUSER_SIZES.map(size => <Option key={size.value} value={size.value}>{size.label}</Option>)}
                                        </Select>
                                    </Form.Item>
                                </Col>

                                <Col xs={12} sm={6} md={6}>
                                    <Form.Item name="status" label="Status" valuePropName="checked">
                                        <Switch defaultChecked />
                                    </Form.Item>
                                </Col>

                                {/* --- BUTTONS --- */}
                                <Col xs={24}>
                                    <Row justify="end" gutter={[10, 10]} style={{ marginTop: 10 }}>
                                        <Col>
                                            <Button onClick={() => navigate('/members/view')}>Cancel</Button>
                                        </Col>
                                        <Col>
                                            <Button type="primary" htmlType="submit">
                                                Update Member
                                            </Button>
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>

                            {/* Tabs for Metrics, Assignments, and Relationships */}
                            <div style={{ marginTop: "40px" }}>
                                <Tabs
                                    defaultActiveKey="1"
                                    items={[
                                        {
                                            key: '1',
                                            label: 'Performance Metrics',
                                            children: (
                                                <>
                                                    <Row justify="end" style={{ marginBottom: 10 }}>
                                                        <Button
                                                            type="primary"
                                                            icon={<PlusOutlined />}
                                                            onClick={openAddMetricsModal}
                                                        >
                                                            Add Metric
                                                        </Button>
                                                    </Row>
                                                    <Table
                                                        columns={metricColumns}
                                                        dataSource={metrics}
                                                        rowKey="id"
                                                        pagination={pagination}
                                                        loading={metricsLoading}
                                                        onChange={handleTableChange}
                                                        bordered
                                                        size="small"
                                                    />
                                                </>
                                            ),
                                        },
                                        {
                                            key: '2',
                                            label: 'Assigned Trainers',
                                            children: (
                                                <>
                                                    <Row justify="end" style={{ marginBottom: 10 }}>
                                                        <Button type="primary" onClick={() => {
                                                            fetchTrainersDropdown();
                                                            setAssignModalOpen(true);
                                                        }}>
                                                            Assign Trainer
                                                        </Button>
                                                    </Row>
                                                    <List
                                                        dataSource={assignedTrainers}
                                                        locale={{ emptyText: "No trainers assigned" }}
                                                        renderItem={item => (
                                                            <List.Item
                                                                actions={[
                                                                    <Popconfirm
                                                                        title="Remove Assignment"
                                                                        description="Are you sure to remove this trainer?"
                                                                        onConfirm={() => handleRemoveTrainer(item.id)}
                                                                        okText="Yes"
                                                                        cancelText="No"
                                                                    >
                                                                        <Button type="primary" danger icon={<DeleteOutlined />} size="small" />
                                                                    </Popconfirm>
                                                                ]}
                                                            >
                                                                <List.Item.Meta
                                                                    title={item.name}
                                                                    description={
                                                                        <div style={{ fontSize: 12 }}>
                                                                            <div>ID: {item.trainer_id} | NIC: {item.nic}</div>
                                                                            <div>{item.contact_no}</div>
                                                                        </div>
                                                                    }
                                                                />
                                                            </List.Item>
                                                        )}
                                                    />
                                                </>
                                            ),
                                        },
                                        {
                                            key: '3',
                                            label: 'Relationships',
                                            children: (
                                                <>
                                                    <Row justify="end" style={{ marginBottom: 10 }}>
                                                        <Button type="primary" icon={<PlusOutlined />} onClick={() => {
                                                            fetchMembersDropdown();
                                                            setRelationshipModalOpen(true);
                                                        }}>
                                                            Add Relationship
                                                        </Button>
                                                    </Row>
                                                    <Table
                                                        dataSource={relationships}
                                                        rowKey="relation_id"
                                                        loading={relationshipsLoading}
                                                        bordered
                                                        size="small"
                                                        columns={[
                                                            {
                                                                title: 'Related Member',
                                                                dataIndex: 'name',
                                                                key: 'name',
                                                                render: (text, record) => (
                                                                    <Link to={`/members/edit/${record.linked_id}`}>{text}</Link>
                                                                )
                                                            },
                                                            {
                                                                title: 'Member ID',
                                                                dataIndex: 'member_id',
                                                                key: 'member_id',
                                                            },
                                                            {
                                                                title: 'NIC',
                                                                dataIndex: 'nic',
                                                                key: 'nic',
                                                            },
                                                            {
                                                                title: 'Relationship',
                                                                dataIndex: 'relation_type',
                                                                key: 'relation_type',
                                                                render: (text) => <Typography.Text strong>{text}</Typography.Text>
                                                            },
                                                            {
                                                                title: 'Actions',
                                                                key: 'actions',
                                                                render: (_, record) => (
                                                                    <Popconfirm
                                                                        title="Remove Relationship"
                                                                        description="Are you sure to remove this relationship?"
                                                                        onConfirm={() => handleRemoveRelationship(record.relation_id)}
                                                                        okText="Yes"
                                                                        cancelText="No"
                                                                    >
                                                                        <Button type="primary" danger icon={<DeleteOutlined />} size="small" />
                                                                    </Popconfirm>
                                                                )
                                                            }
                                                        ]}
                                                    />
                                                </>
                                            ),
                                        },
                                    ]}
                                />
                            </div>
                        </Card>
                    </Form>
                </Spin>

                {/* Metrics Modal (ADDED) */}
                <Modal
                    title={selectedMetricForEdit ? "Edit Metric" : `Add Metric for ${memberName}`}
                    open={isMetricsModalOpen}
                    onCancel={() => setIsMetricsModalOpen(false)}
                    footer={null}
                    destroyOnHidden
                >
                    <Form
                        form={metricsForm}
                        layout="vertical"
                        onFinish={handleMetricsSubmit}
                        onValuesChange={calculateBMI}
                    >
                        <Row gutter={16}>
                            <Col span={24}>
                                <Form.Item
                                    name="date"
                                    label="Date & Time"
                                    rules={[{ required: true, message: 'Please select date and time' }]}
                                >
                                    <DatePicker showTime format="YYYY-MM-DD HH:mm:ss" style={{ width: '100%' }} />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="weight"
                                    label="Weight (kg)"
                                    rules={[{ required: true, message: 'Required' }]}
                                >
                                    <InputNumber placeholder="60kg" style={{ width: '100%' }} min={0} step={0.1} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="height"
                                    label="Height (cm)"
                                    rules={[{ required: true, message: 'Required' }]}
                                >
                                    <InputNumber placeholder="180cm" style={{ width: '100%' }} min={0} step={0.1} />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name="bmi" label="BMI">
                                    <InputNumber style={{ width: '100%' }} readOnly placeholder="Auto-Generated" />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="body_fat" label="Body Fat (%)">
                                    <InputNumber placeholder="20%" style={{ width: '100%' }} min={0} step={0.1} />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name="muscle_mass" label="Muscle Mass (kg)">
                                    <InputNumber placeholder="60kg" style={{ width: '100%' }} min={0} step={0.1} />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row justify="end">
                            <Space>
                                <Button onClick={() => setIsMetricsModalOpen(false)}>Cancel</Button>
                                <Button type="primary" htmlType="submit" loading={loading}>
                                    {selectedMetricForEdit ? "Update" : "Save"}
                                </Button>
                            </Space>
                        </Row>
                    </Form>
                </Modal>

                {/* Add Relationship Modal */}
                <Modal
                    title={`Add Relationship for ${memberName}`}
                    open={relationshipModalOpen}
                    onCancel={() => setRelationshipModalOpen(false)}
                    onOk={handleAddRelationship}
                    confirmLoading={relationFormLoading}
                >
                    <div style={{ marginBottom: 16 }}>
                        <p>Select a member to link:</p>
                        <Select
                            showSearch
                            style={{ width: '100%' }}
                            placeholder="Search and select member"
                            optionFilterProp="children"
                            onChange={(val) => setSelectedLinkedMemberId(val)}
                            onSearch={(val) => fetchMembersDropdown(val)}
                            filterOption={false}
                            notFoundContent={null}
                        >
                            {memberList.map(m => (
                                <Option key={m.id} value={m.id}>
                                    {m.name} ({m.member_id})
                                </Option>
                            ))}
                        </Select>
                    </div>
                    <div>
                        <p>Relationship Type:</p>
                        <Select
                            style={{ width: '100%' }}
                            placeholder="Select relationship type"
                            onChange={(val) => setRelationType(val)}
                        >
                            <Option value="Spouses">Spouses</Option>
                            <Option value="Partners">Partners</Option>
                            <Option value="Siblings">Siblings</Option>
                            <Option value="Friends">Friends</Option>
                            <Option value="Other">Other</Option>
                        </Select>
                    </div>
                </Modal>
            </Content>
            <Footerdiv />
        </Layout>
    );
};